import { useMemo } from 'react';
import { createTheme, makeStyles } from '@material-ui/core/styles';
import { outlinedInputClasses } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTheme } from '@chakra-ui/react';
import ScopeType from '@/utils/enums/ScopeType';
import { WHOLESALER_BANNER_HEIGHT_VAR } from '@/utils/layoutCssVars';

const DRAWER_WIDTH = 240;
const DRAWER_COLLAPSE = 64;
const NAVBAR_HEIGHT = 64;
// (3101): fallback only — WholesalerPaymentBanner overrides this on mount.
const WHOLESALER_BANNER_HEIGHT = 72;
const CONTENT_MARGIN_TOP = 16;

export const shouldShowWholesalerPaymentBanner = (
    requiredScope,
    wholesalerPaymentBanner,
) =>
    requiredScope === ScopeType.WHOLESALER &&
    Boolean(wholesalerPaymentBanner?.enabled);

export const getContentTopOffset = (showWholesalerPaymentBanner) =>
    NAVBAR_HEIGHT +
    (showWholesalerPaymentBanner ? WHOLESALER_BANNER_HEIGHT : 0) +
    CONTENT_MARGIN_TOP;

// (3108): a CSS value rather than a fixed number — tracks the banner's real
// measured height via its CSS var, so a longer banner message pushes content
// down instead of the fixed header covering it.
export const getContentTopOffsetCss = (showWholesalerPaymentBanner) =>
    showWholesalerPaymentBanner
        ? `calc(${NAVBAR_HEIGHT}px + var(${WHOLESALER_BANNER_HEIGHT_VAR}, ${WHOLESALER_BANNER_HEIGHT}px) + ${CONTENT_MARGIN_TOP}px)`
        : `${NAVBAR_HEIGHT + CONTENT_MARGIN_TOP}px`;

export function useThemeHelper() {
    const {
        darkMode,
        mixedMode: lightMode,
        colorScheme,
    } = useSelector((state) => {
        return { ...state.settings };
    });

    const { isMenuCollapse } = useSelector((state) => ({
        isMenuCollapse: state.menu.collapse,
    }));

    const { requiredScope, wholesalerPaymentBanner } = useSelector(
        (state) => state.login,
    );
    const showWholesalerPaymentBanner = shouldShowWholesalerPaymentBanner(
        requiredScope,
        wholesalerPaymentBanner,
    );
    const contentTopOffset = getContentTopOffset(showWholesalerPaymentBanner);

    const { brand } = useTheme().colors;
    //Styles created to be used for react functional components
    const useStyles = makeStyles((theme) => {
        return {
            root: {
                display: 'flex',
                minHeight: '100vh',
                backgroundColor: darkMode
                    ? 'var(--chakra-colors-chakra-body-bg, #121212)'
                    : 'var(--chakra-colors-chakra-body-bg, #ffffff)',
            },
            appBar: {
                zIndex: theme.zIndex.drawer + 1,
                background: 'var(--chakra-colors-dark-bgDark, #121212)',
                borderBottomColor: '#fafafa',
            },
            drawer: {
                width: !isMenuCollapse ? DRAWER_WIDTH : DRAWER_COLLAPSE,
                flexShrink: 0,
            },
            drawerPaper: {
                width: !isMenuCollapse ? DRAWER_WIDTH : DRAWER_COLLAPSE,
                background: 'var(--chakra-colors-dark-bgDark, #121212)',
                boxShadow:
                    '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
                overflowX: 'hidden',
            },
            drawerContainer: {
                overflow: 'auto',
            },
            content: {
                flexGrow: 1,
                // Without an explicit value, this defaults to the table's
                // own (much larger, column-driven) min-content width, which
                // would stop the toolbar/search bar from ever shrinking.
                // 800 is a practical floor: wide enough for busier toolbars
                // (e.g. Users' 5 buttons) plus the collapsed platform filter
                // stack and search box to never look broken, narrow enough
                // that the table still gets real room to shrink first.
                // (3108): drop the floor once it no longer fits beside the drawer, to avoid horizontal overflow on iPad/medium screens.
                minWidth: 800,
                [`@media (max-width:${
                    800 + (!isMenuCollapse ? DRAWER_WIDTH : DRAWER_COLLAPSE)
                }px)`]: {
                    minWidth: 0,
                },
                padding: theme.spacing(3),
                paddingTop: 0,
                // (3101): use the banner's real measured height so a taller
                // banner pushes content down instead of covering it.
                marginTop: showWholesalerPaymentBanner
                    ? `calc(${NAVBAR_HEIGHT}px + var(${WHOLESALER_BANNER_HEIGHT_VAR}, ${WHOLESALER_BANNER_HEIGHT}px) + ${CONTENT_MARGIN_TOP}px)`
                    : contentTopOffset,
            },
        };
    });

    const theme = useMemo(
        () =>
            createTheme({
                typography: {
                    button: {
                        textTransform: 'none',
                    },
                },
                palette: {
                    type: darkMode ? 'dark' : 'light',
                    primary: {
                        ...brand,
                        main: darkMode ? '#039be5' : '#03a9f4',
                    },
                    secondary: {
                        main: '#8cc440',
                    },
                },
                props: {
                    MuiBreadcrumbs: {
                        separator: '>',
                    },
                    MuiCircularProgress: {
                        disableShrink: true,
                    },
                    MuiAlert: {
                        variant: 'filled',
                    },
                    MuiCheckbox: {
                        color: 'secondary',
                    },
                    MuiTextField: {
                        variant: 'outlined',
                        size: 'small',
                    },
                    MuiTabs: {
                        indicatorColor: `var(--chakra-colors-${colorScheme}-400)`,
                    },
                    MuiInputLabel: {
                        shrink: true,
                    },
                    MuiSelect: {
                        variant: 'outlined',
                    },
                    MuiChip: {
                        size: 'small',
                    },
                    MuiOutlinedInput: {
                        notched: true,
                    },
                },
                overrides: {
                    MuiTableCell: {
                        head: {
                            backgroundColor: darkMode
                                ? null
                                : '#f5f5f5 !important',
                        },
                    },
                    MuiTab: {
                        root: {
                            '&$selected': {
                                color: `var(--chakra-colors-${colorScheme}-400)!important`,
                            },
                        },
                    },
                    MuiTablePagination: {
                        root: {
                            border: 'none',
                        },
                        toolbar: {
                            background: darkMode ? 'transparent' : null,
                        },
                    },
                    MuiFormControlLabel: {
                        label: {
                            color: darkMode ? 'white' : null,
                        },
                    },
                    MuiTextField: {
                        root: {
                            padding: '5px',
                            paddingInline: 0,
                            '& label.Mui-focused': {
                                color: `var(--chakra-colors-${colorScheme}-500)`,
                            },
                            '& .MuiInput-underline:after': {
                                borderBottomColor: `var(--chakra-colors-${colorScheme}-500)`,
                            },
                        },
                    },
                    MuiInputLabel: {
                        root: {
                            padding: '5px',
                        },
                    },
                    MuiTabs: {
                        indicatorColor: `var(--chakra-colors-${colorScheme}-50)`,
                    },
                    // override light mode
                    MuiAutocomplete: {
                        option: {
                            '&[aria-selected="true"]': {
                                backgroundColor: `var(--chakra-colors-${colorScheme}-500)`,
                                color: 'white',
                            },
                            '&[data-focus="true"]': {
                                color: 'inherit',
                                backgroundColor: darkMode
                                    ? 'rgba(255, 255, 255, 0.26)'
                                    : 'var(--chakra-colors-gray-100)',
                            },
                        },
                    },
                    MuiOutlinedInput: {
                        root: {
                            '&:hover $notchedOutline': {
                                borderColor: 'var(--chakra-colors-gray-300)',
                            },
                            [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]:
                                {
                                    borderColor: `var(--chakra-colors-${colorScheme}-500)!important`,
                                },
                            '&': {
                                borderRadius: '0.375rem',
                            },
                        },
                    },
                },
            }),
        [brand, colorScheme, darkMode],
    );

    const lightModeTheme = useMemo(
        () =>
            createTheme({
                palette: {
                    type: 'dark',
                    primary: {
                        ...brand,
                    },
                },
                props: {
                    MuiTextField: {
                        variant: 'outlined',
                        size: 'small',
                    },
                },
                overrides: {
                    MuiTableCell: {
                        head: {
                            backgroundColor: darkMode
                                ? null
                                : '#f5f5f5 !important',
                        },
                    },
                    MuiTab: {
                        root: {
                            '&$selected': {
                                color: `var(--chakra-colors-${colorScheme}-400)!important`,
                            },
                        },
                    },
                    MuiTablePagination: {
                        toolbar: {
                            background: darkMode ? '#121212' : null,
                        },
                    },
                    MuiFormControlLabel: {
                        label: {
                            color: darkMode ? 'white' : null,
                        },
                    },
                    MuiTextField: {
                        root: {
                            padding: '5px',
                            paddingInline: 0,
                        },
                    },
                    MuiInputLabel: {
                        root: {
                            padding: '5px',
                        },
                    },
                    MuiTabs: {
                        indicatorColor: `var(--chakra-colors-${colorScheme}-50)`,
                    },
                    MuiAutocomplete: {
                        option: {
                            '&[aria-selected="true"]': {
                                backgroundColor: `var(--chakra-colors-${colorScheme}-500)`,
                                color: 'white',
                            },
                        },
                    },
                    MuiOutlinedInput: {
                        root: {
                            '&:hover $notchedOutline': {
                                borderColor: 'var(--chakra-colors-gray-300)',
                            },
                        },
                    },
                },
            }),
        [brand, colorScheme, darkMode],
    );

    const classes = useStyles();

    return {
        classes,
        mixedMode: lightMode,
        lightModeTheme,
        theme,
    };
}
